<template>
  <content-panel>
    <template v-slot:search>
      <search-box class="search-box">
        <el-form
          :model="filter"
          inline
          size="mini"
          label-width="78px"
          @submit.native.prevent="search"
        >
          <el-form-item label="商品编码">
            <el-input
              v-model.trim="filter.skuCode"
              clearable
              placeholder="请输入商品编码（SKU）"
              @keyup.enter.native="search"
            />
          </el-form-item>
          <el-form-item label="商品名称">
            <el-input
              v-model.trim="filter.skuName"
              clearable
              placeholder="请输入商品名称（SKU）"
              @keyup.enter.native="search"
            />
          </el-form-item>
        </el-form>

        <template v-slot:right>
          <el-button size="mini" icon="el-icon-refresh-left" @click="reset">
            重置
          </el-button>
          <el-button
            class="green-btn"
            size="mini"
            icon="el-icon-search"
            :loading="loading"
            @click="search"
          >
            查询
          </el-button>
        </template>
      </search-box>
    </template>

    <template v-slot:sub>
      <el-alert
        class="fixed-period-tip"
        type="info"
        :closable="false"
        show-icon
      >
        <template v-slot:title>
          <strong>最高优先级：</strong>
          人工配置在生效周期内始终优先于所有自动规则；未填写生效结束日期表示长期生效。
        </template>
      </el-alert>
      <el-alert v-if="error" :title="error" type="error" :closable="false">
        <el-button type="text" @click="initialize">重新加载</el-button>
      </el-alert>
    </template>

    <table-panel ref="tablePanel">
      <template v-slot:header-left>
        <div class="fixed-period-actions">
          <el-button
            class="green-btn"
            size="mini"
            icon="el-icon-plus"
            @click="edit()"
          >
            新增
          </el-button>
          <el-button size="mini" icon="el-icon-upload2" @click="importVisible = true">
            批量导入
          </el-button>
          <el-button
            class="template-button"
            size="mini"
            icon="el-icon-download"
            @click="downLoadTemplate"
          >
            下载模板
          </el-button>
        </div>
      </template>

      <el-table
        v-loading="loading"
        :data="rows"
        :height="maxTableHeight"
        border
        size="small"
        :header-cell-style="{ backgroundColor: '#F4F8FB' }"
        empty-text="暂无符合条件的记录"
        element-loading-text="数据加载中"
      >
        <el-table-column
          prop="skuCode"
          label="商品编码（SKU）"
          min-width="150"
          align="center"
          show-overflow-tooltip
        />
        <el-table-column
          prop="skuName"
          label="商品名称"
          min-width="210"
          align="center"
          show-overflow-tooltip
        />
        <el-table-column
          prop="fixedDeliveryCycle"
          label="固定发货周期"
          min-width="180"
          align="center"
        >
          <template slot-scope="scope">
            <span class="fixed-cycle">{{ periodText(scope.row.fixedDeliveryCycle) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="effectiveStartDate"
          label="生效开始日期"
          min-width="170"
          align="center"
        >
          <template slot-scope="scope">
            {{ fixedDateText(scope.row, 'effectiveStartDate') }}
          </template>
        </el-table-column>
        <el-table-column
          prop="effectiveEndDate"
          label="生效结束日期"
          min-width="170"
          align="center"
        >
          <template slot-scope="scope">
            {{ endDateText(scope.row) }}
          </template>
        </el-table-column>
        <el-table-column
          prop="operatorName"
          label="更新人"
          width="100"
          align="center"
          show-overflow-tooltip
        />
        <el-table-column
          prop="updatedTime"
          label="更新时间"
          min-width="175"
          align="center"
        >
          <template slot-scope="scope">
            {{ updatedTimeText(scope.row.updatedTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" icon="el-icon-edit" @click="edit(scope.row)">
              编辑
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <template v-slot:footer>
        <div class="fixed-period-pagination">
          <el-pagination
            :current-page.sync="page"
            :page-size.sync="size"
            :total="total"
            :page-sizes="[30, 50, 100, 500]"
            layout="total, sizes, prev, pager, next, jumper"
            background
            @size-change="search"
            @current-change="load"
          />
        </div>
      </template>
    </table-panel>

    <el-dialog
      v-if="editVisible"
      :visible.sync="editVisible"
      :title="(form.id ? '编辑' : '新增') + 'SKU固定发货周期'"
      width="560px"
      class="fixed-period-dialog"
      :close-on-click-modal="false"
      :show-close="!saving"
      :close-on-press-escape="!saving"
    >
      <el-form
        :model="form"
        class="fixed-period-form"
        label-width="140px"
        size="small"
        :disabled="saving"
        @submit.native.prevent="save"
      >
        <el-form-item label="商品编码（SKU）" required>
          <el-input
            v-model.trim="form.skuCode"
            :disabled="!!form.id"
            :suffix-icon="skuLoading ? 'el-icon-loading' : ''"
            placeholder="请输入商品编码（SKU）"
            maxlength="128"
            @input="skuChanged"
            @blur="fillSku"
          />
        </el-form-item>
        <el-form-item label="商品名称" required>
          <el-input
            :value="form.skuName"
            placeholder="根据商品编码自动带出"
            readonly
          />
        </el-form-item>
        <el-form-item
          label="固定发货周期"
          required
        >
          <el-input
            :value="form.fixedDeliveryCycle"
            inputmode="numeric"
            placeholder="请输入正整数"
            @input="fixedCycleChanged"
          >
            <template slot="append">
              <span>天</span>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item label="生效开始日期" required>
          <el-date-picker
            v-model="form.effectiveStartDate"
            type="date"
            format="yyyy/MM/dd"
            value-format="yyyy-MM-dd"
            placeholder="请选择生效开始日期"
          />
        </el-form-item>
        <el-form-item label="生效结束日期">
          <el-date-picker
            v-model="form.effectiveEndDate"
            type="date"
            format="yyyy/MM/dd"
            value-format="yyyy-MM-dd"
            placeholder="请选择生效结束日期"
            clearable
          />
          <div class="fixed-period-hint">不填写表示长期生效</div>
        </el-form-item>
      </el-form>

      <el-alert
        v-if="editError"
        :title="editError"
        type="error"
        :closable="false"
      />
      <template v-slot:footer>
        <el-button :disabled="saving" @click="editVisible = false">
          取消
        </el-button>
        <el-button
          type="primary"
          :loading="saving"
          :disabled="skuLoading"
          @click="save"
        >
          保存
        </el-button>
      </template>
    </el-dialog>

    <import-dialog
      v-if="importVisible"
      :visible.sync="importVisible"
      title="SKU 固定发货周期配置导入"
      report-name="deliveryCycleSkuFixedImport"
      @refresh="search"
    />
  </content-panel>
</template>

<script>
import moment from 'moment'
import axios from '@/utils/axiosInstance'
import pagePathMixin from '@/utils/page-path-mixin'
import tableMixin from '@/utils/tableMixin'
import {
  errorText,
  fixedDateText,
  fixedForm,
  fixedPayload,
  pageRequest,
  periodText,
  responseData,
  validateFixed
} from './model'
import ImportDialog from './components/ImportDialog'

const emptyFilter = () => ({
  skuCode: '',
  skuName: ''
})

const emptyForm = () => ({
  skuCode: '',
  skuName: '',
  fixedDeliveryCycle: undefined,
  effectiveStartDate: '',
  effectiveEndDate: '',
  longTermEffective: true
})

export default {
  name: 'SkuFixedPeriod',
  mixins: [pagePathMixin, tableMixin],
  components: { ImportDialog },
  data () {
    return {
      filter: emptyFilter(),
      applied: {},
      rows: [],
      total: 0,
      page: 1,
      size: 30,
      loading: false,
      error: '',
      editError: '',
      requestId: 0,
      skuRequestId: 0,
      skuLoading: false,
      editVisible: false,
      importVisible: false,
      saving: false,
      templateData: '',
      form: emptyForm()
    }
  },
  computed: {
    resizeOffset () {
      return 70
    }
  },
  created () {
    this.getTemplate()
    this.initialize()
  },
  beforeDestroy () {
    this.requestId++
    this.skuRequestId++
  },
  methods: {
    periodText,
    fixedDateText,
    async initialize () {
      this.filter = emptyFilter()
      this.page = 1
      this.size = 30
      await this.search()
    },
    search () {
      this.applied = {
        skuCode: this.filter.skuCode,
        skuName: this.filter.skuName
      }
      this.page = 1
      return this.load()
    },
    reset () {
      this.filter = emptyFilter()
      this.search()
    },
    async load () {
      const id = ++this.requestId
      this.loading = true
      this.error = ''

      try {
        const data = responseData(
          await this.$q.shippingPeriod.fixed({
            data: pageRequest(this.page, this.size, this.applied)
          })
        )

        if (id === this.requestId) {
          this.rows = data.records || []
          this.total = Number(data.total) || 0
        }
      } catch (error) {
        if (id === this.requestId) {
          this.rows = []
          this.total = 0
          this.error = errorText(error)
        }
      } finally {
        if (id === this.requestId) {
          this.loading = false
        }
      }
    },
    endDateText (row) {
      return fixedDateText(row, 'effectiveEndDate') === '长期'
        ? '长期生效'
        : fixedDateText(row, 'effectiveEndDate')
    },
    updatedTimeText (value) {
      if (!value) return value

      const time = moment(value)
      return time.isValid() ? time.format('YYYY-MM-DD HH:mm:ss') : value
    },
    async getTemplate () {
      const res = await axios.get(
        '/qu-platform-excel-api/excel/cfg/getTemplate/deliveryCycleSkuFixedImport'
      )
      this.templateData = res.data
    },
    downLoadTemplate () {
      const loading = this.$loading({
        lock: true,
        text: 'Loading',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      setTimeout(() => {
        this.downLoad(this.templateData.templatePath, `${this.templateData.fileName}.XLSX`)
        loading.close()
      }, 300)
    },
    downLoad (path, name) {
      const xhr = new XMLHttpRequest()
      xhr.open('GET', path, true)
      xhr.responseType = 'blob'
      xhr.onload = function () {
        if (xhr.status === 200) {
          if (window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveBlob(xhr.response, name)
          } else {
            const link = document.createElement('a')
            const body = document.querySelector('body')
            link.href = window.URL.createObjectURL(xhr.response)
            link.download = name
            link.style.display = 'none'
            body.appendChild(link)
            link.click()
            body.removeChild(link)
            window.URL.revokeObjectURL(link.href)
          }
        }
      }
      xhr.send()
    },
    edit (row) {
      this.skuRequestId++
      this.skuLoading = false
      this.form = row ? fixedForm(row) : emptyForm()
      this.editError = ''
      this.editVisible = true
    },
    fixedCycleChanged (value) {
      this.form.fixedDeliveryCycle = String(value == null ? '' : value)
        .replace(/\D/g, '')
        .replace(/^0+/, '')
    },
    skuChanged () {
      this.skuRequestId++
      this.skuLoading = false
      this.form.skuName = ''
      this.editError = ''
    },
    async fillSku () {
      const code = this.form.skuCode
      const id = ++this.skuRequestId
      this.form.skuName = ''
      this.skuLoading = true
      this.editError = ''

      try {
        if (!code) throw new Error('请输入商品编码')

        const data = responseData(
          await this.$q.shippingPeriod.skuInfo({
            params: { skuCode: code }
          })
        )

        if (id === this.skuRequestId) {
          if (!data || !data.skuName) throw new Error('商品编码不存在')
          this.form.skuName = data.skuName
        }
      } catch (error) {
        if (id === this.skuRequestId) {
          this.editError = errorText(error)
        }
      } finally {
        if (id === this.skuRequestId) {
          this.skuLoading = false
        }
      }
    },
    async save () {
      if (this.saving || this.skuLoading) return

      this.form.longTermEffective = !this.form.effectiveEndDate
      this.editError = validateFixed(this.form)

      if (this.editError) return

      this.saving = true

      try {
        const data = fixedPayload(this.form)
        const action = data.id ? 'updateFixed' : 'saveFixed'
        responseData(await this.$q.shippingPeriod[action]({ data }))
        this.editVisible = false
        this.$message.success('保存成功，已立即生效')
        await this.search()
      } catch (error) {
        this.editError = errorText(error)
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style src="./style.css"></style>

<style scoped>
.fixed-period-page {
  padding: 8px;
  background: #fff;
}

.fixed-period-page ::v-deep .search-outer {
  padding: 12px 0;
  border-bottom: 1px solid #ebeef5;
}

.fixed-period-page ::v-deep .sub-area {
  padding: 0;
}

.fixed-period-page ::v-deep .main-area {
  margin-top: 0;
  padding-top: 12px;
}

.fixed-period-tip {
  padding: 10px 16px;
  border-radius: 0;
  background: #f4faf9;
  color: #43828b;
}

.fixed-period-actions .template-button {
  border-color: #13aa8d;
  color: #13aa8d;
}

.fixed-period-page .fixed-cycle {
  color: #d88700;
  font-weight: 600;
}

.fixed-period-page ::v-deep .el-table .el-button--text {
  color: #13aa8d;
}

.fixed-period-pagination {
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
  text-align: left;
}

.fixed-period-dialog ::v-deep .el-dialog__header {
  padding-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}

.fixed-period-dialog ::v-deep .el-dialog__body {
  padding: 24px 40px 8px 20px;
}

.fixed-period-dialog ::v-deep .el-dialog__footer {
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
}

.fixed-period-field {
  display: flex;
  align-items: center;
  gap: 8px;
}

.fixed-period-field .el-input-number {
  flex: 1;
  min-width: 0;
}

.fixed-period-dialog ::v-deep .el-input-number .el-input__inner {
  text-align: left;
}

.fixed-period-hint {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
  line-height: 20px;
}

.fixed-period-dialog .el-form .el-input {
  width: 280px;
}
</style>
